import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Gauge } from "lucide-react";

function fmt(s) {
  if (!s || isNaN(s)) return "00:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function VideoPlayer({ src, poster, startAt = 0, onProgress, onEnded, onStart }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [started, setStarted] = useState(false);
  const lastReport = useRef(0);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onLoaded = () => {
      setDur(v.duration);
      if (startAt > 0 && startAt < v.duration) v.currentTime = startAt;
    };
    v.addEventListener("loadedmetadata", onLoaded);
    return () => v.removeEventListener("loadedmetadata", onLoaded);
  }, [startAt, src]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
      if (!started) { setStarted(true); onStart && onStart(); }
    } else { v.pause(); setPlaying(false); }
  };

  const onTime = () => {
    const v = ref.current;
    setCur(v.currentTime);
    if (onProgress && v.currentTime - lastReport.current >= 5) {
      lastReport.current = v.currentTime;
      onProgress(Math.floor(v.currentTime));
    }
  };

  const seek = (e) => {
    const v = ref.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = pct * dur;
    setCur(v.currentTime);
  };

  const cycleSpeed = () => {
    const opts = [1, 1.25, 1.5, 2, 0.75];
    const next = opts[(opts.indexOf(speed) + 1) % opts.length];
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  const fullscreen = () => {
    if (wrapRef.current?.requestFullscreen) wrapRef.current.requestFullscreen();
  };

  const toggleMute = () => {
    const v = ref.current;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div ref={wrapRef} className="relative w-full rounded-xl overflow-hidden bg-black group" data-testid="video-player">
      <video
        ref={ref}
        src={src}
        poster={poster}
        className="w-full aspect-video bg-black"
        onTimeUpdate={onTime}
        onEnded={() => { setPlaying(false); onEnded && onEnded(); }}
        onClick={toggle}
        playsInline
      />
      {!playing && (
        <button
          onClick={toggle}
          data-testid="video-play-overlay"
          className="absolute inset-0 flex items-center justify-center bg-black/30"
        >
          <span className="h-16 w-16 rounded-full bg-chessup-turq/90 flex items-center justify-center shadow-[0_0_30px_rgba(0,229,212,0.5)]">
            <Play className="h-7 w-7 text-chessup-deep ml-1" fill="currentColor" />
          </span>
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/85 to-transparent">
        <div className="h-1.5 w-full bg-white/20 rounded-full cursor-pointer mb-2" onClick={seek} data-testid="video-progress">
          <div className="h-full bg-chessup-turq rounded-full" style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} />
        </div>
        <div className="flex items-center gap-3 text-chessup-text text-sm">
          <button onClick={toggle} data-testid="video-toggle">{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>
          <button onClick={toggleMute}>{muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}</button>
          <span className="font-mono text-xs">{fmt(cur)} / {fmt(dur)}</span>
          <div className="flex-1" />
          <button onClick={cycleSpeed} className="flex items-center gap-1 text-xs font-mono" data-testid="video-speed">
            <Gauge className="h-4 w-4" /> {speed}x
          </button>
          <button onClick={fullscreen} data-testid="video-fullscreen"><Maximize className="h-5 w-5" /></button>
        </div>
      </div>
    </div>
  );
}
